import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SnackNotifier } from '@app/shared/notifier/snack-notifier';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { Navigate } from '@ngxs/router-plugin';
import { AuthActions} from '@app/modules/auth/store/auth.actions';
import { ILogin } from '@app/modules/auth/interface/login';
import { AUTH_ERROR_CODES } from '@app/modules/auth/conts/error-codes';
import { environment } from '@env/environment';

// Google Identity Services global (loaded via the gsi/client script in index.html).
declare const google: any;

/**
 * Which SSO methods this server actually has credentials for. Read before
 * either button is rendered: a deployment with no GOOGLE_CLIENT_ID or
 * EPIC_CLIENT_ID used to show both anyway, so every click was a dead end.
 * The client id is public by design (it ships in the page of every SSO app);
 * taking it from the server keeps it from drifting out of sync with the
 * credential the backend verifies tokens against.
 */
interface SsoConfig {
  google: { enabled: boolean; clientId: string | null };
  epic: { enabled: boolean };
}

@Component({
  templateUrl: './login.component.html',
  styles: [
    `
      .epic-login-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 320px;
        height: 42px;
        font-size: 14px;
        font-weight: 600;
        color: #fff;
        background: #c8102e;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        box-shadow: 0 1px 2px rgba(200, 16, 46, 0.25);
        transition: background 0.15s;
      }
      .epic-login-btn:hover { background: #a60d26; }
      .epic-mark { font-weight: 700; font-size: 16px; line-height: 1; }
      .mfa-step { max-width: 340px; margin: 0 auto; }
      .mfa-title { font-size: 18px; font-weight: 600; margin-bottom: 6px; color: #16202b; }
      .mfa-sub { font-size: 13px; color: #667; margin-bottom: 18px; }
      .mfa-input {
        width: 100%; height: 48px; text-align: center; letter-spacing: 10px;
        font-size: 22px; font-weight: 600; border: 1px solid #d0d7de; border-radius: 8px;
        padding: 0 12px; margin-bottom: 12px; box-sizing: border-box;
      }
      .mfa-input:focus { outline: none; border-color: #0f6e8c; box-shadow: 0 0 0 2px rgba(15,110,140,.15); }
      .mfa-err { color: #b8433a; font-size: 13px; margin-bottom: 10px; }
      .mfa-btn {
        width: 100%; height: 44px; border: none; border-radius: 8px; cursor: pointer;
        background: #0f6e8c; color: #fff; font-weight: 600; font-size: 15px;
      }
      .mfa-btn:hover { background: #0c586f; }
    `,
  ],
})
export class LoginPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('googleBtn') private googleBtn: ElementRef;

  private _userEmail: string = null;
  private _subscription = new Subscription();

  /** SSO availability, resolved from the server; both hidden until it answers. */
  public googleEnabled = false;
  public epicEnabled = false;

  /** True once the backend signals the account needs a second factor. */
  public mfaRequired = false;
  public mfaError: string = null;
  private _pendingCreds: ILogin = null;

  constructor(
    private readonly store: Store,
    private readonly actions: Actions,
    private readonly snack: SnackNotifier,
    private readonly zone: NgZone,
    private readonly http: HttpClient,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.subscribeOnLoginFailed();
    this.loadSsoConfig();
  }

  public ngAfterViewInit(): void {
    // The Google button is rendered by loadSsoConfig, once we know whether
    // this server can honour it and which client id to use.
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public login(value: ILogin): void {
    this._userEmail = value.email;
    this._pendingCreds = value;
    this.store.dispatch(new AuthActions.Login(value));
  }

  /** Resubmit the stored credentials together with the authenticator code. */
  public submitTotp(code: string): void {
    if (!this._pendingCreds || !code) {
      return;
    }
    this.mfaError = null;
    this.store.dispatch(new AuthActions.Login({ ...this._pendingCreds, totp: code.trim() }));
  }

  /** Begin the Epic SMART login (full-page redirect to the backend, then Epic). */
  public loginWithEpic(): void {
    window.location.href = `${environment.apiUrl}/epic/login`;
  }

  /**
   * Ask the server which SSO methods are configured. On any failure both stay
   * hidden: password login always works, so an unreachable config endpoint
   * must not put a button on screen that we cannot promise will work.
   */
  private loadSsoConfig(): void {
    this.http.get<SsoConfig>(`${environment.apiUrl}/auth/sso-config`).subscribe(
      (config) => {
        this.epicEnabled = !!config?.epic?.enabled;
        this.googleEnabled = !!config?.google?.enabled && !!config?.google?.clientId;
        this.changeDetector.detectChanges();
        if (this.googleEnabled) {
          this.initGoogleButton(config.google.clientId as string);
        }
      },
      () => {
        this.epicEnabled = false;
        this.googleEnabled = false;
        this.changeDetector.detectChanges();
      },
    );
  }

  /** Render the "Sign in with Google" button once the GIS script is ready. */
  private initGoogleButton(clientId: string, attempt = 0): void {
    if (typeof google === 'undefined' || !google.accounts || !this.googleBtn) {
      if (attempt < 40) {
        setTimeout(() => this.initGoogleButton(clientId, attempt + 1), 150);
      }
      return;
    }

    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: { credential: string }) => this.onGoogleCredential(response),
    });
    google.accounts.id.renderButton(this.googleBtn.nativeElement, {
      theme: 'outline',
      size: 'large',
      width: 320,
      text: 'signin_with',
      shape: 'rectangular',
    });
  }

  /** GIS callback fires outside Angular's zone; re-enter it to dispatch. */
  private onGoogleCredential(response: { credential: string }): void {
    this.zone.run(() => {
      this.store.dispatch(new AuthActions.GoogleLogin(response.credential));
    });
  }

  private subscribeOnLoginFailed(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(AuthActions.LoginFailed))
        .subscribe(({ error }) => {
          const message = error?.error?.message ?? error?.message;

          // Account has MFA on: switch to the code step instead of showing an error.
          // Wrap state changes in the zone — the NGXS action stream can resolve
          // outside Angular's change detection, so the view wouldn't update.
          if (message === 'MFA_REQUIRED') {
            this.zone.run(() => {
              this.mfaRequired = true;
              this.mfaError = null;
            });
            return;
          }
          // Wrong code while on the MFA step: keep the step, show a hint.
          if (this.mfaRequired && message === 'Invalid authentication code.') {
            this.zone.run(() => (this.mfaError = 'That code was not valid. Try again.'));
            return;
          }

          this.snack.error(error);
          if ((error as { code?: string }).code === AUTH_ERROR_CODES.UserNotConfirmedException) {
            this.store.dispatch(new Navigate(['/auth/registration-confirm'], { email: this._userEmail }));
          }
        }),
    );
  }
}

// epic login wired
